import { useEffect, useRef, useState } from 'react'
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
} from 'lightweight-charts'
import { fetchMarketAnalysis } from '../../api/market'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import type { MarketAnalysis } from '../../types/market'
import type { LpState } from '../../types/lp'

const ASSET      = 'SOL'
const PROVIDER   = 'binance'
const REFRESH_MS = 60_000

const TIMEFRAMES = ['1h', '4h', '1d', '1w'] as const
type Timeframe   = typeof TIMEFRAMES[number]

const CHART_BG       = '#0F1424'
const GRID_COLOR     = "#1F2747"
const TEXT_COLOR     = '#8B8FA3'
const UP_COLOR       = '#22c55e'
const DOWN_COLOR     = '#ef4444'
const SUPPORT_COLOR  = '#22c55e'
const RESIST_COLOR   = '#ef4444'
const VOL_UP_COLOR   = '#22c55e55'
const VOL_DOWN_COLOR = '#ef444455'

// Distinct palette for concentrated LP price-range bands.
// Reserved: green (support), red (resistance) — do not reuse here.
const LP_RANGE_COLORS = [
  '#60a5fa', // blue-400
  '#a78bfa', // violet-400
  '#f59e0b', // amber-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#fb7185', // rose-400
  '#facc15', // yellow-400
] as const

const CONCENTRATED_PROVIDERS = new Set(['raydium', 'orca'])
const USD_QUOTES = ['USDC', 'USDT', 'USD']

/** Returns true if the pool price range is quoted in USD per ASSET (chart-aligned). */
function isChartAlignedPair(pair: string, asset: string): boolean {
  if (!pair) return false
  const [left, right] = pair.split('/')
  return left?.toUpperCase() === asset.toUpperCase() && USD_QUOTES.includes(right?.toUpperCase())
}

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: Math.max(decimals, 4),
  })
}

/** Rampage bull icon — svgrepo.com */
function BullIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" className={className} fill="currentColor" aria-hidden="true">
      <path d="M509.988,75.807c-6,3.281-26.203,9.156-56-3.563c-30.078-12.828-61.031,6.141-72.438,15.422l-31.828-28.594
        c-1.188-1.078-2.734-1.656-4.344-1.641l-95.891,1.375c-1.844,0.031-3.594,0.859-4.766,2.25l-70.203,83.094
        c-0.813,0.969-1.906,1.672-3.125,2.016l-75.171,21.266c-1.453,0.422-2.719,1.344-3.563,2.609l-31.813,48.047
        c-3.969-0.031-7.359-0.75-10.25-1.984c-4.359-1.891-7.672-4.906-10.016-8.781c-2.328-3.875-3.641-8.641-3.641-13.766
        c0.016-6.844,2.313-14.266,7.469-20.969c5.141-6.688,13.141-12.703,24.828-16.531c15.797-5.172,27.281-10.922,35.688-16.703
        c18.078-1.984,21.859-17.547,21.906-30.125c0.078-0.797,0.125-1.531,0.125-2.109c0-0.797-0.063-1.359-0.094-1.625l-0.125,0.016
        c-0.375-7.125-1.703-12.547-1.703-12.547s-0.641,5.734-11.984,10.781c-16.422,4.703-21.047,17.438-19.266,26.797
        c-6.891,4.219-16.156,8.609-28.734,12.719c-13.953,4.547-24.422,12.125-31.313,21.125c-6.922,8.984-10.266,19.313-10.266,29.172
        c0,9.844,3.344,19.328,9.844,26.438c3.25,3.547,7.297,6.469,11.969,8.484c3.688,1.578,7.828,2.391,12.172,2.75l-0.891,72.422
        c0,0.391-0.047,0.781-0.125,1.172l-11.625,58.141c-0.156,0.75-0.422,1.453-0.828,2.094L0.942,435.182
        c-0.984,1.594-1.203,3.547-0.609,5.328c0.578,1.781,1.938,3.203,3.672,3.906l24.328,9.703c2.406,0.953,5.156,0.359,6.953-1.5
        l51.141-53.328c0.047-0.031,0.078-0.063,0.109-0.094l56.046-56.063l134.672-74.063l20.156,27.484
        c1.125,1.531,1.5,3.469,1.047,5.313c-0.469,1.828-1.719,3.375-3.422,4.188l-43.375,20.828c-1.531,0.719-2.719,2.047-3.266,3.672
        c-0.563,1.594-0.438,3.375,0.328,4.906l5.703,11.375c1.484,2.969,5.016,4.313,8.094,3.047l87.906-35.969
        c3.047-1.234,4.641-4.609,3.672-7.75l-19.766-64.234c-0.594-1.984-0.219-4.141,1.047-5.766l5.453-7.047
        c1.125-1.453,2.813-2.344,4.625-2.469c1.828-0.109,3.609,0.563,4.906,1.859l27.672,27.563c1.734,1.734,4.297,2.297,6.609,1.484
        l31.531-11.156c2.219-0.781,3.828-2.719,4.172-5.047l13.563-89.781c26.859-1.438,28.516-24.781,28.516-24.781l-37.031-7.922
        c6.234-5.828,14.813-12.391,23.766-14.844c13.219-3.609,28.906,0.094,45.156-6.406C510.566,91.104,515.128,74.198,509.988,75.807z"/>
    </svg>
  )
}

/** Bear icon — svgrepo.com */
function BearIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg viewBox="14 66 125 85" className={className} fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M130.81 101.16L93.81 111.91L85.35 129.81L85.08 140.81C89.15 141.57 90.17 144.97 89.94 147.65L76.94 147.5L68.56 131.2L69.56 118.93L42 127L33.85 140.78C40.85 143.11 38.25 148.21 38.25 148.21L22.37 148.13L16.43 131.13C16.43 131.13 23.96 105.87 26.57 96.9C30.62 82.8 40.47 73.41 57.26 74C72.5 74.51 92.12 76.18 101.18 76.87C104.5 67.87 111.41 70.61 111.41 70.61L112.33 78.05C120 79.68 124.03 83.24 124.52 88.28L133.61 90.42L130.81 101.16ZM57.52 127.75L56.77 139.59C60.64 139.98 63.12 143.91 63.12 147.85H46.33L40.9 137.15L45 131.43L57.52 127.75ZM108.74 129.75C112.37 135.24 105.02 144.62 105.02 144.62L102.35 143.51C102.35 143.51 103.58 136.12 98.53 134.07C93.48 132.02 91.26 130.84 91.26 130.84L99.59 115.84C99.59 115.84 106.5 126.4 108.74 129.79V129.75Z"/>
    </svg>
  )
}

interface LiveMarketPanelProps {
  lpPositions?: LpState[]
}

export default function LiveMarketPanel({ lpPositions }: LiveMarketPanelProps = {}) {
  const [timeframe, setTimeframe] = useState<Timeframe>('4h')
  const [analysis, setAnalysis]   = useState<MarketAnalysis | null>(null)
  const [error, setError]         = useState<string | null>(null)
  const [loading, setLoading]     = useState(false)
  const shimmerRef = useShimmerOnUpdate(lpPositions)

  const chartPaneRef = useRef<HTMLDivElement>(null)
  const chartRef     = useRef<IChartApi | null>(null)
  const candleRef    = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const volRef       = useRef<ISeriesApi<'Histogram'> | null>(null)
  const supportRef   = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']> | null>(null)
  const resistRef    = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']> | null>(null)
  const lpRangeLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([])

  // Create chart once
  useEffect(() => {
    const container = chartPaneRef.current
    if (!container) return

    const chart = createChart(container, {
      layout:    { background: { color: CHART_BG }, textColor: TEXT_COLOR },
      grid:      { vertLines: { color: GRID_COLOR }, horzLines: { color: GRID_COLOR } },
      rightPriceScale: { borderColor: GRID_COLOR },
      timeScale: { borderColor: GRID_COLOR, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
      width:  container.clientWidth  || 400,
      height: container.clientHeight || 220,
    })

    const candle = chart.addSeries(CandlestickSeries, {
      upColor: UP_COLOR, downColor: DOWN_COLOR,
      borderVisible: false, wickUpColor: UP_COLOR, wickDownColor: DOWN_COLOR,
    })
    const vol = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' }, priceScaleId: 'vol',
    } as Parameters<typeof chart.addSeries<'Histogram'>>[1])
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } })

    chartRef.current  = chart
    candleRef.current = candle
    volRef.current    = vol

    const ro = new ResizeObserver(() => {
      if (!chartPaneRef.current || !chartRef.current) return
      chartRef.current.applyOptions({
        width:  chartPaneRef.current.clientWidth,
        height: chartPaneRef.current.clientHeight || 220,
      })
    })
    ro.observe(container)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current   = null
      candleRef.current  = null
      volRef.current     = null
      supportRef.current = null
      resistRef.current  = null
      lpRangeLinesRef.current = []
    }
  }, [])

  // Auto-fetch on mount + when timeframe changes + every REFRESH_MS
  useEffect(() => {
    let alive = true
    setLoading(true)
    const run = () => {
      fetchMarketAnalysis(ASSET, timeframe, PROVIDER)
        .then(data => { if (alive) { setAnalysis(data); setError(null) } })
        .catch(err => { if (alive) setError(err instanceof Error ? err.message : 'fetch failed') })
        .finally(() => { if (alive) setLoading(false) })
    }
    run()
    const id = setInterval(run, REFRESH_MS)
    return () => { alive = false; clearInterval(id) }
  }, [timeframe])

  // Paint chart whenever analysis updates
  useEffect(() => {
    if (!analysis || !candleRef.current || !volRef.current) return

    type CP = { time: number; open: number; high: number; low: number; close: number }
    type VP = { time: number; value: number; color: string }

     
    candleRef.current.setData(analysis.candles.map<CP>(c => ({ time: c.timestamp, open: c.open, high: c.high, low: c.low, close: c.close })) as any)
     
    volRef.current.setData(analysis.candles.map<VP>(c => ({ time: c.timestamp, value: c.volume, color: c.close >= c.open ? VOL_UP_COLOR : VOL_DOWN_COLOR })) as any)

    if (supportRef.current) { candleRef.current.removePriceLine(supportRef.current); supportRef.current = null }
    if (resistRef.current)  { candleRef.current.removePriceLine(resistRef.current);  resistRef.current  = null }

    supportRef.current = candleRef.current.createPriceLine({ price: analysis.nearestSupport,    color: SUPPORT_COLOR, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `S $${fmt(analysis.nearestSupport)}` })
    resistRef.current  = candleRef.current.createPriceLine({ price: analysis.nearestResistance, color: RESIST_COLOR,  lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `R $${fmt(analysis.nearestResistance)}` })

    chartRef.current?.timeScale().fitContent()
  }, [analysis])

  // Paint concentrated LP price ranges — one color band per position
  useEffect(() => {
    const candle = candleRef.current
    if (!candle) return

    // Remove prior range lines
    for (const line of lpRangeLinesRef.current) candle.removePriceLine(line)
    lpRangeLinesRef.current = []

    if (!lpPositions || lpPositions.length === 0) return

    const concentrated = lpPositions.filter(p =>
      CONCENTRATED_PROVIDERS.has(p.provider) && isChartAlignedPair(p.pair, ASSET)
    )

    concentrated.forEach((pos, idx) => {
      const lower = parseFloat(pos.priceLower)
      const upper = parseFloat(pos.priceUpper)
      if (!isFinite(lower) || !isFinite(upper) || lower <= 0 || upper <= 0) return

      const color = LP_RANGE_COLORS[idx % LP_RANGE_COLORS.length]
      const tag   = `${pos.pair}${concentrated.length > 1 ? ` #${idx + 1}` : ''}`

      const lowerLine = candle.createPriceLine({
        price: lower, color, lineWidth: 1, lineStyle: LineStyle.Dotted,
        axisLabelVisible: true, title: `${tag} ↓ $${fmt(lower)}`,
      })
      const upperLine = candle.createPriceLine({
        price: upper, color, lineWidth: 1, lineStyle: LineStyle.Dotted,
        axisLabelVisible: true, title: `${tag} ↑ $${fmt(upper)}`,
      })
      lpRangeLinesRef.current.push(lowerLine, upperLine)
    })
  }, [lpPositions, analysis])

  // Header price MUST match the chart's last candle close, not the EMA-smoothed currentPrice,
  // and updates together with the chart since both are re-derived from the same analysis payload.
  const lastCandle = analysis?.candles.length ? analysis.candles[analysis.candles.length - 1] : null
  const headerPrice = lastCandle?.close ?? analysis?.currentPrice ?? null
  const bullish     = analysis?.bullish ?? false
  const predDelta   = analysis ? analysis.predictedReturnPct * 100 : 0
  const deltaColor  = analysis == null ? 'text-gray-500'
                    : predDelta >= 0   ? 'text-emerald-400'
                                       : 'text-red-400'

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-fringe shrink-0">
        {analysis ? (
          bullish
            ? <BullIcon className="w-8 h-8 text-emerald-400" />
            : <BearIcon className="w-8 h-8 text-red-400" />
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-6 h-6 text-gray-500">
            <polyline points="2 17 8 11 13 16 22 7"/>
          </svg>
        )}
        <span className="text-sm font-semibold text-ink">{ASSET}</span>

        {/* Timeframe toggles */}
        <div className="flex gap-0.5 bg-surface-hover/60 rounded-md p-0.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors
                ${timeframe === tf ? 'bg-blue-600 text-ink' : 'text-ink-muted hover:text-ink'}`}
            >
              {tf}
            </button>
          ))}
        </div>

        {analysis && (
          <>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${bullish ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {bullish ? 'BULLISH' : 'BEARISH'}
            </span>
            {headerPrice != null && (
              <span className="ml-auto text-sm font-semibold text-ink tabular-nums">${fmt(headerPrice)}</span>
            )}
            <span className={`text-xs font-medium tabular-nums ${deltaColor}`}>
              {predDelta >= 0 ? '+' : ''}{predDelta.toFixed(2)}%
            </span>
          </>
        )}
      </div>

      {/* Body: chart + compact stats strip */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="relative flex-1 min-h-[200px]" ref={chartPaneRef}>
          {loading && !analysis && !error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-gray-400 text-xs">Loading market data…</span>
            </div>
          )}
          {error && !analysis && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-red-400 text-xs px-4 text-center">{error}</span>
            </div>
          )}
        </div>

        {analysis && (
          <div className="grid grid-cols-4 gap-0 border-t border-fringe text-center shrink-0">
            <Stat label="Support"   value={`$${fmt(analysis.nearestSupport)}`}    color="text-emerald-400" />
            <Stat label="Resist."   value={`$${fmt(analysis.nearestResistance)}`} color="text-red-400" />
            <Stat label="RSI"       value={fmt(analysis.rsi, 1)}
                  color={analysis.rsi < 30 ? 'text-amber-400' : analysis.rsi > 70 ? 'text-red-400' : 'text-ink'} />
            <Stat label="Breakout"  value={`${Math.round(analysis.breakoutProbability * 100)}%`} color="text-ink" />
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, color = 'text-ink' }: { label: string; value: string; color?: string }) {
  return (
    <div className="py-1.5 border-l border-fringe first:border-l-0">
      <div className="text-[9px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-[11px] font-semibold ${color}`}>{value}</div>
    </div>
  )
}
